<template>
  <!-- Teleported to the body and positioned with `fixed`: the plan and the
       toolbar both live inside transformed ancestors, and a transform makes
       itself the containing block for position:fixed descendants — which is
       what used to send tooltips and select dropdowns to the wrong corner. -->
  <Teleport to="body">
    <div
      v-if="open"
      ref="panel"
      class="fixed z-50"
      :style="style"
      v-bind="$attrs"
    >
      <slot />
    </div>
  </Teleport>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref, watch, nextTick } from 'vue'

defineOptions({ inheritAttrs: false })

const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  /**
   * What to hang off: an element, or a getter returning one, or a plain
   * rect for anchors that are not DOM nodes (canvas shapes, for instance).
   */
  anchor: {
    type: [Object, Function],
    default: null,
  },
  /** bottom | top | left | right, each optionally -start or -end. */
  placement: {
    type: String,
    default: 'bottom',
  },
  offset: {
    type: Number,
    default: 8,
  },
  /** Keeps the panel this far inside the viewport edges. */
  padding: {
    type: Number,
    default: 8,
  },
  /** Flip to the opposite side when the preferred one does not fit. */
  flip: {
    type: Boolean,
    default: true,
  },
})

const panel = ref(null)
const style = ref({ top: '0px', left: '0px' })

const anchorRect = () => {
  const target =
    typeof props.anchor === 'function' ? props.anchor() : props.anchor

  if (!target) {
    return null
  }

  return typeof target.getBoundingClientRect === 'function'
    ? target.getBoundingClientRect()
    : target
}

const place = (side, align, anchor, size) => {
  const { offset } = props

  const main = {
    bottom: { top: anchor.bottom + offset, left: anchor.left },
    top: { top: anchor.top - size.height - offset, left: anchor.left },
    right: { top: anchor.top, left: anchor.right + offset },
    left: { top: anchor.top, left: anchor.left - size.width - offset },
  }[side]

  const horizontal = side === 'top' || side === 'bottom'

  if (horizontal) {
    if (align === 'end') {
      main.left = anchor.right - size.width
    } else if (!align) {
      main.left = anchor.left + anchor.width / 2 - size.width / 2
    }
  } else if (align === 'end') {
    main.top = anchor.bottom - size.height
  } else if (!align) {
    main.top = anchor.top + anchor.height / 2 - size.height / 2
  }

  return main
}

const fits = (position, size) =>
  position.top >= props.padding &&
  position.left >= props.padding &&
  position.top + size.height <= window.innerHeight - props.padding &&
  position.left + size.width <= window.innerWidth - props.padding

const opposite = { bottom: 'top', top: 'bottom', left: 'right', right: 'left' }

const update = () => {
  const anchor = anchorRect()

  if (!anchor || !panel.value) {
    return
  }

  const size = panel.value.getBoundingClientRect()
  const [side, align] = props.placement.split('-')

  let position = place(side, align, anchor, size)

  // Flip before shifting: a panel that would hang off the bottom belongs
  // above its anchor, not squashed against the edge next to it.
  if (props.flip && !fits(position, size)) {
    const flipped = place(opposite[side], align, anchor, size)

    if (fits(flipped, size)) {
      position = flipped
    }
  }

  // Shift whatever is left back inside the viewport.
  const maxLeft = window.innerWidth - size.width - props.padding
  const maxTop = window.innerHeight - size.height - props.padding

  style.value = {
    left: `${Math.round(Math.max(props.padding, Math.min(position.left, maxLeft)))}px`,
    top: `${Math.round(Math.max(props.padding, Math.min(position.top, maxTop)))}px`,
  }
}

let observer = null

const track = () => {
  // Capture phase: the plan and its panels scroll in their own containers,
  // not only on the window.
  window.addEventListener('scroll', update, true)
  window.addEventListener('resize', update)

  if (typeof ResizeObserver !== 'undefined' && panel.value) {
    observer = new ResizeObserver(update)
    observer.observe(panel.value)
  }
}

const untrack = () => {
  window.removeEventListener('scroll', update, true)
  window.removeEventListener('resize', update)
  observer?.disconnect()
  observer = null
}

watch(
  () => props.open,
  async (open) => {
    if (!open) {
      untrack()

      return
    }

    // Measure after the panel exists, or its size is still zero.
    await nextTick()
    update()
    track()
  },
  { immediate: true },
)

watch(() => [props.placement, props.anchor], update)

onMounted(() => props.open && update())
onBeforeUnmount(untrack)

defineExpose({ update })
</script>
